import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, useCycle } from 'motion/react';
import { Link } from 'react-router-dom';
import { ContentfulButton, ContentfulMedia } from '@wbk/contentful';
import { WebsiteConfig } from '@wbk/contentful/api';
import { ChevronIcon, CloseIcon } from '@wbk/svg';
import { Button, ImageWithPlaceholder, Typography } from '@wbk/ui';

import LanguageSwitch from '@/components/common/LanguageSwitch';
import useLocalization from '@/context/localization/useLocalization';
import useIsCurrentPath from '@/hooks/useIsCurrentPath';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import { SearchButton } from '@/components/common/GlobalSearch';

import { router } from '@/routes';
import Timer from './Timer';
import ContactModal, { ContactButton } from './ContactModal';

type Props = {
  config: WebsiteConfig;
  toggleOpenGlobalSearch: () => void;
  isGlobalSearchOpen?: boolean;
  scrolled: boolean;
};

const MobileMenu = ({ config, toggleOpenGlobalSearch, isGlobalSearchOpen, scrolled }: Props) => {
  const { t } = useTranslation(['common']);
  const [isOpen, toggleOpen] = useCycle(false, true);
  const { locale } = useLocalization();
  const [showZones, toggleShowZones] = useCycle(true, false);
  const [showTimer, toggleShowTimer] = useCycle(true, false);
  const [isContactModalOpen, toggleContactModal] = useCycle(false, true);

  const getTicket = config?.header?.topSubNavigationCollection?.items[0];

  const { lock, unlock } = useScrollLock({
    autoLock: false,
  });

  const laylatPath = `/${locale}/laylat-alomr`;
  const ticketPath = `/${locale}/hamza-cairokee-marwan-concert-rs25-tickets`;
  const shouldHideLaylat = useIsCurrentPath(laylatPath);
  const shouldHideTicket = useIsCurrentPath(ticketPath);

  const shouldHideTicketOrLaylat = shouldHideTicket || shouldHideLaylat;

  useEffect(() => {
    if (isOpen) {
      lock();
      toggleShowTimer(1);
    } else {
      unlock();

      // Delay showing content until overlay animation completes
      const timeout = setTimeout(() => {
        toggleShowTimer(0);
      }, 400); // Match the overlay exit duration (400ms)

      return () => clearTimeout(timeout);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this when isOpen changes, adding
  }, [isOpen]); // lock and toggleShowTimer will cause unwanted behavior

  useEffect(() => {
    if (!isGlobalSearchOpen && isOpen) {
      toggleOpen(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to run this when isGlobalSearchOpen changes, adding isOpen will cause unwanted behavior
  }, [isGlobalSearchOpen]);

  return (
    <div className='absolute start-0 top-10 z-20 w-full p-4 sm:px-10 sm:py-5 md:top-4 md:px-8 lg:hidden'>
      <div className='relative h-full w-full'>
        <div className='absolute inset-x-0 top-0 flex -translate-y-5 items-center justify-center py-2'>
          <button
            className='pointer-events-auto px-2'
            type='button'
            onClick={() => {
              toggleOpen();
            }}
          >
            {isOpen ? <CloseIcon /> : <img src='/icons/MenuIcon.svg' alt='' />}
          </button>
        </div>

        {showTimer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='flex flex-col items-center justify-center gap-4'
          >
            <div
              className={twMerge(
                'flex w-full items-center justify-between gap-4 px-6 pt-2',
                'max-xsx:pe-3'
              )}
            >
              <Link to={`/${locale}`} className='relative h-12 w-20'>
                {scrolled ? (
                  <div className='vibrant-glass-logo' aria-label='Logo' />
                ) : (
                  <ImageWithPlaceholder src='/images/logo.svg' alt='Logo' className='w-20' />
                )}
              </Link>
              {!shouldHideTicketOrLaylat && (
                <Button
                  className={twMerge(
                    'text-body-XS rounded-full px-4 py-1 font-semibold rtl:flex-row-reverse',
                    'max-xsx:px-2'
                  )}
                >
                  <Link to={getTicket?.href || '/'} target='_blank' rel='noreferrer noopener'>
                    {getTicket?.label}
                  </Link>
                  <img src='/icons/ShareIcon.svg' alt='' />
                </Button>
              )}
            </div>

            <Timer config={config} />
          </motion.div>
        )}

        {/* overlay menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key='mobile-menu-overlay'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className='relative h-full w-full'
            >
              <MenuOverlaySvg className='pointer-events-none w-full -translate-y-3 md:-translate-y-6 2xl:translate-y-0' />

              <ul className='container pointer-events-auto absolute inset-x-0 top-10 z-[99] h-[88dvh] space-y-6 overflow-y-auto px-6'>
                <li className='flex flex-col gap-6 border-b border-white/20 pb-6'>
                  {config.header.topNavigationCollection.items.map((link) => {
                    if (link.id === 'zones') {
                      return (
                        <li
                          key={link.id}
                          className='flex w-full flex-col gap-4 border-b border-white/20 pb-4'
                        >
                          <button
                            type='button'
                            className='!text-body-M flex w-full items-center justify-between gap-3 py-2 font-semibold uppercase text-white'
                            onClick={() => {
                              toggleShowZones();
                            }}
                          >
                            <button
                              onClick={() => {
                                // go to zones page
                                router.navigate(`/${locale}/zones`);
                                // hide menu
                                toggleOpen();
                              }}
                            >
                              {t('common:zones')}
                            </button>

                            <ChevronIcon
                              className={twMerge(
                                'transition-transform duration-300',
                                showZones ? '-rotate-90' : 'rotate-90'
                              )}
                            />
                          </button>
                          {/* zones breadcrumb */}
                          <AnimatePresence initial={true}>
                            {showZones && (
                              <motion.ul
                                key='zones-submenu'
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                className='grid grid-cols-4 gap-6 overflow-hidden px-4'
                              >
                                {config.header.zonesCollection?.items?.map((zone) => {
                                  if (!zone) return null;

                                  return (
                                    <li key={zone.slug}>
                                      <Link
                                        to={`/${locale}/zones/${zone.slug}`}
                                        className='transition hover:grayscale-[60%]'
                                        onClick={() => toggleOpen()}
                                      >
                                        <div className='space-y-1'>
                                          <ContentfulMedia
                                            media={zone?.zoneLogo}
                                            alt={zone.title}
                                            width={100}
                                            height={100}
                                            fit='pad'
                                          />
                                          {zone?.sponsorLogo && (
                                            <ContentfulMedia
                                              className='mx-auto max-h-5 w-auto'
                                              media={zone?.sponsorLogo}
                                              alt={zone.sponsorLogo?.title || ''}
                                            />
                                          )}
                                        </div>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </li>
                      );
                    }
                    return (
                      <Typography key={link.id}>
                        <Link
                          to={`/${locale}${link.href}`}
                          onClick={() => {
                            toggleOpen();
                          }}
                          className='!text-body-M font-semibold uppercase text-white'
                        >
                          {link.label}
                        </Link>
                      </Typography>
                    );
                  })}
                </li>
                <li className='flex flex-col gap-6'>
                  <div className='flex flex-col gap-2 md:flex-row'>
                    <div className='flex flex-1 gap-2'>
                      <LanguageSwitch className='border-primary rtl:!text-body-XS text-primary shrink-0 rounded-full border px-6 py-1 font-semibold' />
                      <Button className='!text-body-XS flex-1 shrink-0 whitespace-nowrap rounded-full px-6 font-semibold rtl:flex-row-reverse'>
                        <Link to={getTicket?.href || '/'} target='_blank' rel='noreferrer noopener'>
                          {getTicket?.label}
                        </Link>
                        <img src='/icons/ShareIcon.svg' alt='' />
                      </Button>
                    </div>
                    <div className='flex flex-1 gap-2'>
                      <ContactButton
                        onClick={() => {
                          toggleContactModal();
                          toggleOpen(0);
                        }}
                      />
                      <SearchButton className='shrink-0' onClick={() => toggleOpenGlobalSearch()} />
                    </div>
                  </div>
                </li>
                <li className='flex flex-col gap-6 pb-10 pt-6'>
                  <div className='flex items-center justify-between gap-4'>
                    <Link to={`/${locale}`}>
                      <ImageWithPlaceholder src='/images/logo.svg' alt='Logo' className='w-20' />
                    </Link>
                    <ul className='flex shrink-0 items-center gap-4'>
                      {config.socialLinks?.iconLinksCollection?.items.map((link) => (
                        <li key={link.id} className='rounded-md'>
                          <ContentfulButton
                            link={link}
                            rel='noreferrer noopener'
                            className='social_media_follow size-8 overflow-hidden rounded-md !p-1'
                            data-location='footer'
                            data-category={link.label}
                            rounded
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ContactModal open={isContactModalOpen} onOpenChange={toggleContactModal} />
    </div>
  );
};

export default MobileMenu;

export const MenuOverlaySvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    // width={402}
    // height={820}
    viewBox='0 0 402 820'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M402 796C402 809.255 391.255 820 378 820H24C10.7452 820 0 809.255 0 796V24C0 10.7451 10.7452 0 24 0H165C171.627 0 177 5.37258 177 12C177 18.6274 182.373 24 189 24H213C219.627 24 225 18.6274 225 12C225 5.37258 230.373 0 237 0H378C391.255 0 402 10.7452 402 24V796Z'
      fill='#18181B'
    />
  </svg>
);
